import React from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import { FormikProps } from 'formik';

import {
    ColumnDto,
    DataSourceDto,
    OrderByDtoDirectionEnum,
} from '../../../../../../../typescript-axios';
import { isFormFieldInvalid, getFormErrorMessage } from '../../../utils/form_utils';
import { StringSchema } from 'yup';

interface StepOrderByProps {
    formik: FormikProps<{ dataSource: DataSourceDto }>;
    /** Combined columns + aggregate op columns — from getOrderByColumnOptions() */
    columnOptions: Record<string, { table: string; column: string }>;
}

export const StepOrderBy: React.FC<StepOrderByProps> = ({ formik, columnOptions }) => {
    const { t } = useTranslation();

    const orderByColumns = formik.values.dataSource.query_definition.order_by?.columns ?? [];

    const selectedColumnKeys = orderByColumns
        .filter((col: { table: string; column: string }) => col != null)
        .map((col) =>
            Object.keys(columnOptions).find(
                (key) =>
                    columnOptions[key].table === col.table &&
                    columnOptions[key].column === col.column
            )
        )
        .filter((key): key is string => key !== undefined);

    const currentDirection = formik.values.dataSource.query_definition.order_by?.direction;
    const hasColumns = selectedColumnKeys.length > 0;

    const handleColumnsChange = (selectedKeys: string[]) => {
        const newColumns = selectedKeys
            .map((key) => columnOptions[key])
            .filter((col): col is { table: string; column: string } => Boolean(col));

        formik.setFieldValue(
            'dataSource.query_definition.order_by',
            newColumns.length > 0
                ? { columns: newColumns, ...(currentDirection ? { direction: currentDirection } : {}) }
                : undefined
        );
    };

    const handleDirectionChange = (direction: OrderByDtoDirectionEnum) => {
        const currentCols = formik.values.dataSource.query_definition.order_by?.columns;
        formik.setFieldValue(
            'dataSource.query_definition.order_by',
            { ...(currentCols ? { columns: currentCols } : {}), direction }
        );
        formik.setFieldTouched('dataSource.query_definition.order_by.direction', true);
    };

    return (
        <div className="formgrid grid col-12">

            {/* Column MultiSelect */}
            <div className="field col-12">
                <label htmlFor="input-create-datasource-order_by" className="font-medium">
                    {t('pages.dashboards.datasource.select_columns')}
                </label>
                <MultiSelect
                    id="input-create-datasource-select_order_by_columns"
                    name="select_order_by_columns"
                    value={selectedColumnKeys}
                    options={Object.keys(columnOptions)}
                    onChange={(e) => handleColumnsChange(e.value)}
                    onBlur={() =>
                        formik.setFieldTouched('dataSource.query_definition.order_by', true)
                    }
                    display="chip"
                    className={
                        isFormFieldInvalid('dataSource.query_definition.order_by.columns', formik.touched, formik.errors)
                            ? 'p-invalid'
                            : ''
                    }
                    style={{ width: '100%' }}
                />
                {isFormFieldInvalid('dataSource.query_definition.order_by.columns', formik.touched, formik.errors) && (
                    <small className="p-error block mt-1">
                        {getFormErrorMessage('dataSource.query_definition.order_by.columns', formik.touched, formik.errors)}
                    </small>
                )}
            </div>

            {/* Direction toggle */}
            <div className="field col-12">
                <label className="font-medium">
                    {t('pages.dashboards.datasource.order_by_direction')}
                </label>
                <div className={`flex flex-wrap gap-2 ${!hasColumns ? 'opacity-50 pointer-events-none' : ''}`}>
                    {Object.values(OrderByDtoDirectionEnum).map((dir) => {
                        const selected = currentDirection === dir;
                        return (
                            <div
                                key={dir}
                                className={`
                                    flex align-items-center gap-2 px-3 py-2
                                    border-round-lg cursor-pointer border-2
                                    transition-all transition-duration-150
                                    ${selected
                                        ? 'border-primary bg-primary-50'
                                        : 'border-200 surface-card hover:border-primary-300'
                                    }
                                `}
                                onClick={() => handleDirectionChange(dir)}
                            >
                                <span className={`font-medium text-sm ${selected ? 'text-primary' : 'text-700'}`}>
                                    {dir.toUpperCase()}
                                </span>
                                {selected && (
                                    <i className="pi pi-check text-xs text-primary" />
                                )}
                            </div>
                        );
                    })}
                </div>
                {isFormFieldInvalid('dataSource.query_definition.order_by.direction', formik.touched, formik.errors) && (
                    <small className="p-error block mt-1">
                        {getFormErrorMessage('dataSource.query_definition.order_by.direction', formik.touched, formik.errors)}
                    </small>
                )}
            </div>
        </div>
    );
};